<script lang="ts" setup>
import { getDate, getVariantShippingDaysDifference } from '../../utils/helpers';
import type { ProductVariant, VariantWithDate } from '~/types/Product.ts';
import { ExpressBadge } from '@laam/ui/base';
import { PhTruck } from '@phosphor-icons/vue';

interface EstimatedShippingDateProps {
	handlingDate: string;
	selectedVariantData: ProductVariant;
	defaultDate: string;
	tags: string[];
}
const props = defineProps<EstimatedShippingDateProps>();

const variantsWithDates: Ref<VariantWithDate[]> = inject('variantsWithDates')!;
const productDate: Ref<string> = inject('productDate')!;

const remainingDays = ref<number | null>(null);

watch(
	() => props.selectedVariantData,
	(newVal: ProductVariant) => {
		if (variantsWithDates.value.length && Object.keys(newVal || {}).length) {
			const date = variantsWithDates.value.find(
				(item) => item!.variant === newVal.title,
			)?.shippingDate;
			if (date) calculateShippingDate(date);
		}
	},
);

const calculateShippingDate = (date: string) => {
	productDate.value = getDate(date);
	remainingDays.value = getVariantShippingDaysDifference(date);
};
const tagsLowerCase = computed(() =>
	props.tags.map((tag) => tag.toLowerCase()),
);
onMounted(() => {
	calculateShippingDate(props.defaultDate);
});

onBeforeMount(() => {
	calculateShippingDate(props.defaultDate);
});
</script>

<template>
	<div
		class="rounded-medium lg:rounded-xl flex flex-row justify-between w-full text-sm bg-gray-50 py-lg px-xl"
	>
		<div
			v-if="
				remainingDays! < 2 &&
				tagsLowerCase.includes('deliverydays_readytoship_rdlrg')
			"
			class="flex gap-xxs items-center"
		>
			<ExpressBadge />
			<span class="text-gray-900 italic font-bold lg:text-sm text-xs">
				shipping
			</span>
		</div>
		<div v-else class="flex items-center gap-xs">
			<PhTruck :size="18" :color="'#141414'" />
			<span class="lg:text-sm text-xs font-normal text-gray-900">
				Standard Shipping
			</span>
		</div>

		<div
			class="gap-xs flex lg:text-sm text-gray-900 font-normal items-center"
			:class="{ 'text-xs': isMobileView() }"
		>
			<span>Ship by </span>
			<span class="date font-semibold text-gray-900">{{ productDate }}</span>
		</div>
	</div>
</template>
